/**
 * Companion task-tray service (CABINET) — COPILOT_MASTER_PLAN Phase 1 / subsystem E + I.
 *
 * Cabinet mirror of the client's copilot-tasks.service. Reads the user's
 * Companion/agentic tasks for the TaskTray UI.
 *
 * Endpoint strategy (feature-detected once per session, then cached):
 *   1. LIVE tray API  GET /agentic/copilot-tasks/      (sgrc_copilot_tasks, paged,
 *      newest first, JWT identity — items carry title/kind/origin/status/
 *      progress_pct/credit_cost/backend_task_id/result_ref/error_message)
 *   2. Fallback       GET /agentic/tasks/              (durable sgrc_agentic.tasks)
 *   3. Neither live → 'none' → the tray renders a graceful empty state and the
 *      poll interval backs off.
 *
 * Cancel cascade: POST /agentic/copilot-tasks/{id}/cancel → DELETE /agentic/tasks/{id}.
 *
 * Real-time: one WS attempt per session to /notifications/ws?token=<jwt>
 * (task.created|task.progress|task.hitl_pending|task.completed|task.failed|
 * task.cancelled). The gateway WS proxy may not be deployed everywhere yet, so
 * the 15s polling path stays as the feature-detected fallback.
 */

import { get, post, del } from '@/lib/api/client'
import { resolveApiUrl } from '@/lib/api/config'
import { keycloak } from '@/lib/auth/keycloak'

// ── View-model ────────────────────────────────────────────────────────────────

export type CompanionTaskStatus =
  | 'QUEUED'
  | 'RUNNING'
  | 'HITL'
  | 'DONE'
  | 'FAILED'
  | 'CANCELLED'
  | 'TIMEOUT'

export interface CompanionTask {
  id: string
  title: string
  goal?: string
  kind?: string
  origin?: string
  mode?: string
  runtime?: string
  /** Durable engine task id backing this tray row (used for cancel fallback). */
  backendTaskId?: string | null
  status: CompanionTaskStatus
  /** 0-100 when known, null → indeterminate while RUNNING. */
  progressPct: number | null
  stepsTotal?: number | null
  stepsDone?: number | null
  /** AI credits (1 credit = $0.01). */
  creditsUsed: number | null
  creditsEstimated?: number | null
  /** result_ref (route/entity ref) or result payload for "open result". */
  resultSummary?: unknown
  error?: string | null
  createdAt?: string
  updatedAt?: string
  completedAt?: string | null
  /** Which backend generation served this row. */
  source: 'tray' | 'agentic'
  /** True while the task can still be cancelled. */
  cancellable: boolean
}

export type TaskSource = 'tray' | 'agentic' | 'none'

export interface CompanionTasksResult {
  tasks: CompanionTask[]
  source: TaskSource
}

// ── Source detection cache (per SPA session) ─────────────────────────────────

let detectedSource: TaskSource | 'unknown' = 'unknown'

/** Test hook / manual reset (e.g. after a backend deploy). */
export function resetCompanionTaskSource(): void {
  detectedSource = 'unknown'
}

function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status
}

/** 404/405/501 = endpoint not (yet) deployed → try the next generation. */
function isMissingEndpoint(err: unknown): boolean {
  const s = httpStatus(err)
  return s === 404 || s === 405 || s === 501
}

const ACTIVE_STATUSES: CompanionTaskStatus[] = ['QUEUED', 'RUNNING', 'HITL']

function normalizeStatus(raw: unknown): CompanionTaskStatus {
  const s = String(raw ?? '').toUpperCase()
  if (['QUEUED', 'RUNNING', 'HITL', 'DONE', 'FAILED', 'CANCELLED', 'TIMEOUT'].includes(s)) {
    return s as CompanionTaskStatus
  }
  if (s === 'PENDING' || s === 'SCHEDULED') return 'QUEUED'
  if (s === 'IN_PROGRESS' || s === 'STARTED') return 'RUNNING'
  if (s === 'COMPLETED' || s === 'SUCCESS') return 'DONE'
  if (s === 'ERROR') return 'FAILED'
  return 'QUEUED'
}

type Row = Record<string, unknown>

function asItems(payload: unknown): Row[] | null {
  if (Array.isArray(payload)) return payload as Row[]
  if (payload && typeof payload === 'object') {
    const obj = payload as Row
    for (const key of ['items', 'tasks', 'results', 'data']) {
      if (Array.isArray(obj[key])) return obj[key] as Row[]
    }
  }
  return null
}

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  // NUMERIC columns serialize as strings (e.g. credit_cost: "2.0000").
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    if (Number.isFinite(n)) return n
  }
  return null
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined
}

/** Normalize a LIVE tray row (/agentic/copilot-tasks/ shape). */
function fromTrayRow(row: Row): CompanionTask {
  const status = normalizeStatus(row.status)
  const stepsTotal = num(row.steps_total)
  const stepsDone = num(row.steps_done)
  let progressPct = num(row.progress_pct)
  if (progressPct == null && stepsTotal && stepsTotal > 0 && stepsDone != null) {
    progressPct = Math.round((stepsDone / stepsTotal) * 100)
  }
  if (status === 'DONE') progressPct = 100
  return {
    id: String(row.id ?? row.task_id ?? ''),
    title: str(row.title) ?? str(row.goal) ?? str(row.kind) ?? 'Tâche Companion',
    goal: str(row.goal),
    kind: str(row.kind),
    origin: str(row.origin),
    mode: str(row.mode),
    runtime: str(row.runtime),
    backendTaskId: str(row.backend_task_id) ?? null,
    status,
    progressPct,
    stepsTotal,
    stepsDone,
    creditsUsed: num(row.credit_cost) ?? num(row.credits_used),
    creditsEstimated: num(row.credits_estimated),
    resultSummary: row.result_ref ?? row.result_summary ?? row.result ?? null,
    error: str(row.error_message) ?? str(row.error) ?? null,
    createdAt: str(row.created_at),
    updatedAt: str(row.updated_at),
    completedAt: str(row.completed_at) ?? null,
    source: 'tray',
    cancellable: ACTIVE_STATUSES.includes(status),
  }
}

/** Normalize a durable agentic row (sgrc_agentic.tasks fallback shape). */
function fromAgenticRow(row: Row): CompanionTask {
  const status = normalizeStatus(row.status)
  const costUsd = num(row.total_cost_usd)
  return {
    id: String(row.id ?? row.task_id ?? ''),
    title: str(row.title) ?? str(row.goal) ?? str(row.skill_id) ?? 'Tâche agentique',
    goal: str(row.goal),
    origin: str(row.origin) ?? 'agent',
    mode: str(row.mode),
    runtime: 'durable',
    backendTaskId: null,
    status,
    // The durable table has no progress_pct → indeterminate while active.
    progressPct: status === 'DONE' ? 100 : num(row.progress_pct),
    stepsTotal: null,
    stepsDone: null,
    // 1 credit = $0.01 (billing convention) when only USD cost is present.
    creditsUsed: num(row.credits_used) ?? (costUsd != null ? Math.round(costUsd * 100) : null),
    creditsEstimated: null,
    resultSummary: row.result ?? row.output ?? row.result_summary ?? null,
    error: str(row.error) ?? str(row.error_message) ?? null,
    createdAt: str(row.created_at),
    updatedAt: str(row.updated_at),
    completedAt: str(row.completed_at) ?? null,
    source: 'agentic',
    cancellable: ACTIVE_STATUSES.includes(status),
  }
}

function sortNewestFirst(tasks: CompanionTask[]): CompanionTask[] {
  return tasks.sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
}

// ── Fetch (with one-time source detection) ────────────────────────────────────

async function fetchFromTray(): Promise<CompanionTask[] | null> {
  const payload = await get<unknown>('/agentic/copilot-tasks/')
  const items = asItems(payload)
  if (items == null) return null // stub/garbage shape → treat as not-landed
  return sortNewestFirst(items.map(fromTrayRow).filter((t) => t.id))
}

async function fetchFromAgentic(): Promise<CompanionTask[] | null> {
  const payload = await get<unknown>('/agentic/tasks/')
  const items = asItems(payload)
  if (items == null) return null
  return sortNewestFirst(items.map(fromAgenticRow).filter((t) => t.id))
}

/**
 * Fetch the user's Companion tasks. Never throws — a failed/absent backend
 * yields `{tasks: [], source: 'none'}` so the tray degrades gracefully.
 */
export async function fetchCompanionTasks(): Promise<CompanionTasksResult> {
  // Fast path: source already known.
  if (detectedSource === 'tray' || detectedSource === 'unknown') {
    try {
      const tasks = await fetchFromTray()
      if (tasks != null) {
        detectedSource = 'tray'
        return { tasks, source: 'tray' }
      }
    } catch (err) {
      if (!isMissingEndpoint(err) && detectedSource === 'tray') {
        // Known-good endpoint had a transient error — keep the source.
        return { tasks: [], source: 'tray' }
      }
      // 404/405/501 (or first-probe failure) → fall through to the legacy list.
    }
  }

  if (detectedSource !== 'none') {
    try {
      const tasks = await fetchFromAgentic()
      if (tasks != null) {
        detectedSource = 'agentic'
        return { tasks, source: 'agentic' }
      }
    } catch (err) {
      if (!isMissingEndpoint(err) && detectedSource === 'agentic') {
        return { tasks: [], source: 'agentic' }
      }
    }
  }

  detectedSource = 'none'
  return { tasks: [], source: 'none' }
}

// ── Cancel ────────────────────────────────────────────────────────────────────

/**
 * Cancel a task. Tries the tray cancel first, then the durable DELETE
 * (against the backing durable task when the tray row references one).
 * Returns true when a backend acknowledged the cancellation.
 */
export async function cancelCompanionTask(task: CompanionTask): Promise<boolean> {
  if (task.source === 'tray') {
    try {
      await post(`/agentic/copilot-tasks/${task.id}/cancel`, {})
      return true
    } catch (err) {
      if (!isMissingEndpoint(err)) return false
      // tray cancel not deployed here yet → try the durable engine cancel
    }
  }
  try {
    await del(`/agentic/tasks/${task.backendTaskId ?? task.id}`)
    return true
  } catch {
    return false
  }
}

// ── Real-time task events (WS, feature-detected; polling stays as fallback) ──

export type RealtimeStatus = 'unknown' | 'active' | 'unavailable'

let realtimeStatus: RealtimeStatus = 'unknown'
let realtimeSocket: WebSocket | null = null
const realtimeListeners = new Set<() => void>()

export function getRealtimeStatus(): RealtimeStatus {
  return realtimeStatus
}

// The Companion can mount BEFORE the keycloak adapter has hydrated its token
// from storage; retry the (single) WS connection a few times until the token
// exists instead of silently never getting realtime for the session.
let realtimeTokenRetries = 0
const REALTIME_TOKEN_MAX_RETRIES = 6

function maybeConnectRealtime(): void {
  if (realtimeStatus !== 'unknown' || typeof WebSocket === 'undefined') return
  if (realtimeListeners.size === 0) return

  if (!keycloak.token) {
    if (realtimeTokenRetries < REALTIME_TOKEN_MAX_RETRIES) {
      realtimeTokenRetries += 1
      setTimeout(maybeConnectRealtime, 5_000)
    }
    return
  }

  try {
    const httpUrl = resolveApiUrl('/notifications/ws')
    const wsUrl = `${httpUrl.replace(/^http/, 'ws')}?token=${encodeURIComponent(keycloak.token)}`
    const socket = new WebSocket(wsUrl)
    realtimeSocket = socket
    let opened = false

    socket.onopen = () => {
      opened = true
      realtimeStatus = 'active'
    }
    socket.onmessage = (evt) => {
      try {
        const data = JSON.parse(String(evt.data)) as { kind?: string; type?: string }
        const kind = data.kind ?? data.type ?? ''
        if (kind.startsWith('task.')) {
          realtimeListeners.forEach((fn) => fn())
        }
      } catch {
        /* non-JSON frame — ignore */
      }
    }
    socket.onerror = () => {
      if (!opened) realtimeStatus = 'unavailable'
    }
    socket.onclose = () => {
      realtimeSocket = null
      // A dropped socket reverts to polling silently (no reconnect storm;
      // the next full page load retries).
      realtimeStatus = 'unavailable'
    }
  } catch {
    realtimeStatus = 'unavailable'
  }
}

/**
 * Subscribe to Companion task lifecycle events (task.created / task.progress /
 * task.hitl_pending / task.completed / task.failed / task.cancelled) over the
 * notification WS. ONE connection attempt per SPA session: if the gateway WS
 * proxy isn't deployed yet the socket fails once, we mark realtime
 * 'unavailable' and the 15s polling path carries the tray. Returns an
 * unsubscribe function.
 */
export function subscribeCompanionTaskEvents(onEvent: () => void): () => void {
  realtimeListeners.add(onEvent)
  maybeConnectRealtime()
  return () => {
    realtimeListeners.delete(onEvent)
    if (realtimeListeners.size === 0 && realtimeSocket) {
      try {
        realtimeSocket.close()
      } catch {
        /* already closed */
      }
      realtimeSocket = null
      if (realtimeStatus === 'active') realtimeStatus = 'unknown'
    }
  }
}
